import { useMemo, useState } from 'react';
import { computeTax } from '../taxEngine.js';
import { COUNTRY_LIST } from '../data/countries.js';
import { formatAmount, formatPercent } from '../utils/format.js';

const MEDALS = ['🥇', '🥈', '🥉'];

export default function CountryComparison({ _currentSalary, currentCountry, _currentPeriod, convert, displayCurrency }) {
  const [sortBy, setSortBy] = useState('net');

  const rows = useMemo(() => {
    return COUNTRY_LIST.map(({ code, name, flag, currency, defaultSalary }) => {
      // Use a normalized salary — convert user's salary to this country's currency if possible
      const salaryToUse = defaultSalary;
      const result = computeTax(code, salaryToUse, 'annual', null, 'single');
      if (!result) return null;

      const netUSD = convert ? convert(result.net, currency, 'USD') : result.net;
      const grossUSD = convert ? convert(result.gross, currency, 'USD') : result.gross;

      return {
        code, name, flag, currency,
        netPercent: result.netPercent,
        effectiveRate: result.effectiveRate,
        netUSD,
        grossUSD,
        defaultGross: defaultSalary,
        defaultCurrency: currency,
        result
      };
    }).filter(Boolean);
  }, [convert]);

  const sorted = useMemo(() => {
    const r = [...rows];
    if (sortBy === 'net') r.sort((a, b) => b.netPercent - a.netPercent);
    else if (sortBy === 'tax') r.sort((a, b) => a.effectiveRate - b.effectiveRate);
    else if (sortBy === 'netUSD') r.sort((a, b) => b.netUSD - a.netUSD);
    return r;
  }, [rows, sortBy]);

  const _displayCurr = displayCurrency || 'USD';

  return (
    <div className="country-comparison" id="compare">
      <div className="country-comparison__header">
        <div>
          <h2 className="country-comparison__title">Global Tax Comparison</h2>
          <p className="country-comparison__subtitle">See how each country&apos;s tax system affects take-home pay, using each country&apos;s typical national salary as benchmark.</p>
        </div>
        <div className="country-comparison__sort">
          <label className="country-comparison__sort-label">Sort by:</label>
          <div className="country-comparison__sort-btns">
            {[
              { key: 'net', label: '% Take-Home' },
              { key: 'tax', label: 'Tax Rate' },
              { key: 'netUSD', label: 'Net (USD)' }
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`country-comparison__sort-btn${sortBy === key ? ' active' : ''}`}
                onClick={() => setSortBy(key)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="country-comparison__table-wrap">
        <table className="country-comparison__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Country</th>
              <th>Benchmark Salary</th>
              <th>Effective Tax Rate</th>
              <th>Net Take-Home %</th>
              <th>Annual Net (USD est.)</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr
                key={row.code}
                className={`country-comparison__row${row.code === currentCountry ? ' current' : ''}`}
              >
                <td className="country-comparison__rank">
                  {i < 3 ? MEDALS[i] : <span className="country-comparison__rank-num">{i + 1}</span>}
                </td>
                <td className="country-comparison__country">
                  <span className="country-comparison__flag">{row.flag}</span>
                  <span className="country-comparison__name">{row.name}</span>
                  {row.code === currentCountry && <span className="country-comparison__current-badge">You</span>}
                </td>
                <td className="country-comparison__salary">
                  {formatAmount(row.defaultGross, row.defaultCurrency)}
                </td>
                <td className="country-comparison__tax-rate">
                  <span className="country-comparison__rate-value" style={{ color: row.effectiveRate > 35 ? '#ff6b6b' : row.effectiveRate > 25 ? '#ffb86c' : '#50fa7b' }}>
                    {formatPercent(row.effectiveRate)}
                  </span>
                  <div className="country-comparison__rate-bar">
                    <div className="country-comparison__rate-bar-fill" style={{ width: `${Math.min(row.effectiveRate, 60)}%`, background: row.effectiveRate > 35 ? '#ff6b6b' : row.effectiveRate > 25 ? '#ffb86c' : '#50fa7b' }} />
                  </div>
                </td>
                <td className="country-comparison__net-pct">
                  <strong style={{ color: '#c9a440' }}>{formatPercent(row.netPercent)}</strong>
                </td>
                <td className="country-comparison__net-usd">
                  {formatAmount(row.netUSD, 'USD')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="country-comparison__note">
        ℹ Comparison uses each country&apos;s typical benchmark salary in local currency with standard filing status. For your personal comparison, adjust the calculator above and note the effective tax rate.
      </p>
    </div>
  );
}

import PropTypes from 'prop-types';

CountryComparison.propTypes = {
  _currentSalary:  PropTypes.number,
  currentCountry:  PropTypes.string.isRequired,
  _currentPeriod:  PropTypes.string,
  convert:         PropTypes.func,
  displayCurrency: PropTypes.string,
}

CountryComparison.defaultProps = {
  currentSalary:   0,
  currentPeriod:   'annual',
  convert:         undefined,
  displayCurrency: 'USD',
}